import { Check, GripHorizontal, Hourglass } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import {
  siGmail,
  siGooglecalendar,
  siGoogledocs,
  siGoogledrive,
  siGooglesheets,
  siNotion,
  siTelegram,
  type SimpleIcon,
} from "simple-icons";

import { SectionHeading, SETTLE_SPRING } from "./SectionHeading";

interface MockEmailProps {
  from: string;
  subject: string;
  tag: "sent" | "held";
  tagLabel: string;
}

function MockEmail({ from, subject, tag, tagLabel }: MockEmailProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-2.5">
      <div className="min-w-0">
        <p className="truncate font-body text-[13px] font-bold text-home-ink">{from}</p>
        <p className="truncate font-body text-xs text-home-ink/65">{subject}</p>
      </div>
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 font-body text-[11px] font-bold whitespace-nowrap text-home-ink ${
          tag === "sent" ? "bg-home-sky" : "bg-home-butter"
        }`}
      >
        {tag === "sent" ? (
          <Check className="h-3 w-3" strokeWidth={3} />
        ) : (
          <Hourglass className="h-3 w-3" strokeWidth={2.5} />
        )}
        {tagLabel}
      </span>
    </div>
  );
}

/**
 * The confident, shipped set only — never list a tool we can't connect.
 * Icons come from simple-icons (CC0) with each brand's own color; the label
 * stays next to the mark so the list never becomes a logo quiz.
 */
const CONNECTED_TOOLS: readonly { icon: SimpleIcon; label: string }[] = [
  { icon: siGmail, label: "Gmail" },
  { icon: siGoogledocs, label: "Google Docs" },
  { icon: siGooglesheets, label: "Sheets" },
  { icon: siGoogledrive, label: "Drive" },
  { icon: siGooglecalendar, label: "Calendar" },
  { icon: siNotion, label: "Notion" },
  { icon: siTelegram, label: "Telegram" },
];

function BrandMark({ icon }: { icon: SimpleIcon }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-[15px] w-[15px] shrink-0"
      fill={`#${icon.hex}`}
      aria-hidden="true"
    >
      <path d={icon.path} />
    </svg>
  );
}

export function EmailLineSection() {
  const { t } = useTranslation("home");

  return (
    <section className="bg-home-cream-deep px-5 py-20 md:py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-12 md:grid-cols-2 md:gap-16">
        <div>
          <SectionHeading kicker={t("emailLine.kicker")} title={t("emailLine.title")} align="left" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ ...SETTLE_SPRING, delay: 0.1 }}
            className="mt-6 space-y-5 font-body text-[16px] leading-relaxed text-home-ink/75"
          >
            <p>{t("emailLine.bodyOne")}</p>
            <p className="font-medium text-home-ink/85">{t("emailLine.bodyTwo")}</p>
          </motion.div>
        </div>

        {/* Inbox with the adjustable line */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ ...SETTLE_SPRING, delay: 0.12 }}
          // cream, not cream-deep: the section is cream-deep, and the card
          // must stay one tone apart — deep section < cream card < white rows.
          className="rounded-[24px] bg-home-cream/80 p-5 shadow-[0_28px_60px_-30px_rgba(33,31,28,0.4)]"
        >
          <p className="mb-3 font-body text-[11px] font-bold tracking-[0.14em] text-home-ink/65 uppercase">
            {t("emailLine.demo.inboxTitle")}
          </p>

          <div className="space-y-2">
            <MockEmail
              from={t("emailLine.demo.emailOneFrom")}
              subject={t("emailLine.demo.emailOneSubject")}
              tag="sent"
              tagLabel={t("emailLine.demo.sentTag")}
            />
            <MockEmail
              from={t("emailLine.demo.emailTwoFrom")}
              subject={t("emailLine.demo.emailTwoSubject")}
              tag="sent"
              tagLabel={t("emailLine.demo.sentTag")}
            />
          </div>

          {/* The line — looks draggable */}
          <motion.div
            initial={{ y: -6 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 120, damping: 10, delay: 0.5 }}
            className="relative my-4 flex cursor-ns-resize items-center gap-3"
            aria-hidden="true"
          >
            <span className="h-0.5 flex-1 rounded-full border-t-2 border-dashed border-home-ink/40" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-home-ink px-3.5 py-1.5 font-body text-[12px] font-bold text-home-cream shadow-[0_8px_20px_-8px_rgba(33,31,28,0.5)]">
              <GripHorizontal className="h-3.5 w-3.5" strokeWidth={2.5} />
              {t("emailLine.demo.lineLabel")}
            </span>
            <span className="h-0.5 flex-1 rounded-full border-t-2 border-dashed border-home-ink/40" />
          </motion.div>

          <div className="space-y-2">
            <MockEmail
              from={t("emailLine.demo.emailThreeFrom")}
              subject={t("emailLine.demo.emailThreeSubject")}
              tag="held"
              tagLabel={t("emailLine.demo.heldTag")}
            />
            <MockEmail
              from={t("emailLine.demo.emailFourFrom")}
              subject={t("emailLine.demo.emailFourSubject")}
              tag="held"
              tagLabel={t("emailLine.demo.heldTag")}
            />
          </div>
        </motion.div>
      </div>

      {/* The hands: tools it already acts in */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ ...SETTLE_SPRING, delay: 0.1 }}
        className="mx-auto mt-12 max-w-3xl text-center"
      >
        <p className="font-body text-[11px] font-bold tracking-[0.16em] text-home-ink/65 uppercase">
          {t("emailLine.tools.label")}
        </p>
        <ul className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {CONNECTED_TOOLS.map(({ icon, label }) => (
            <li
              key={label}
              className="inline-flex items-center gap-2 rounded-full bg-white py-1.5 pr-4 pl-3 font-body text-[13px] font-semibold text-home-ink shadow-[0_8px_20px_-12px_rgba(33,31,28,0.35)]"
            >
              <BrandMark icon={icon} />
              {label}
            </li>
          ))}
          <li className="rounded-full border border-dashed border-home-ink/30 px-4 py-1.5 font-body text-[13px] font-medium text-home-ink/65">
            {t("emailLine.tools.more")}
          </li>
        </ul>
        <p className="mt-4 font-body text-[14px] font-medium text-home-ink/65">
          {t("emailLine.tools.body")}
        </p>
      </motion.div>
    </section>
  );
}
